import { Link } from "@/components/LocaleLink";
import { useI18n } from "@/i18n/I18nProvider";
import { italianName, localizedName } from "@/i18n/content";
import { CheckCircle, Clock } from "lucide-react";
import type { Material } from "@/hooks/useBioNexusData";
import { countMeasured, degradationLabel, isCompleteData } from "@/lib/materials";
import { cn } from "@/lib/utils";

/** Tiny nine-dot readiness indicator for cards. */
export const TRLDots = ({ level }: { level: number }) => {
  const { m } = useI18n();
  return (
  <span className="inline-flex items-center gap-2" aria-label={m.trl.aria(level, m.trl.stages[level - 1] ?? "")} title={m.trl.dotsTitle}>
    <span className="flex gap-[2px]">
      {Array.from({ length: 9 }).map((_, i) => (
        <span key={i} className={cn("h-1.5 w-2.5 rounded-full", i < level ? "bg-primary" : "bg-muted-foreground/20")} />
      ))}
    </span>
    <span className="font-mono text-xs text-muted-foreground">TRL {level}</span>
  </span>
  );
};

/** Small glassy badge that sits on top of an image. */
export const ImageBadge = ({ material }: { material: Partial<Material> }) => {
  const { m } = useI18n();
  const measured = countMeasured(material);
  if (!measured) return null;
  const complete = isCompleteData(material);
  const label = m.materials.measured(measured);
  return (
    <span
      title={complete ? m.materials.measuredTitle : undefined}
      className="inline-flex items-center gap-1.5 rounded-full bg-card/90 backdrop-blur-md px-3 py-1 text-xs text-foreground/80">
      {complete ? <CheckCircle className="w-3 h-3 text-primary" /> : <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/50" />}
      {label}
    </span>
  );
};

interface MaterialCardProps {
  material: Material;
  resolveImage?: (url?: string | null) => string | null | undefined;
  size?: "default" | "large";
}

const MaterialCard = ({ material, resolveImage = (u) => u, size = "default" }: MaterialCardProps) => {
  const { locale } = useI18n();
  const name = localizedName(locale, material.name, material.name_cn, italianName(material, "name"));
  const degradation = degradationLabel(material);
  const image = resolveImage(material.image_url);

  return (
    <Link
      to={`/materials/${material.id}`}
      className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border/70 bg-card transition-[border-color,transform,box-shadow] duration-500 ease-expo-out hover:-translate-y-1 hover:border-primary/40 hover:shadow-[0_24px_50px_-30px_rgba(0,0,0,0.45)]"
    >
      <div className={cn("relative overflow-hidden bg-muted", size === "large" ? "aspect-[16/10]" : "aspect-[16/10] sm:aspect-[4/3]")}>
        {image ? (
          <img
            src={image}
            alt={material.name}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-700 ease-expo-out group-hover:scale-105"
          />
        ) : (
          <div className="h-full w-full bg-gradient-to-br from-primary/20 to-muted" />
        )}
        <div className="absolute left-3 top-3">
          <ImageBadge material={material} />
        </div>
      </div>

      <div className={cn("flex flex-1 flex-col", size === "large" ? "p-7" : "p-6")}>
        {material.family && (
          <p lang="en" className="text-xs uppercase tracking-[0.12em] text-primary line-clamp-1">{material.family}</p>
        )}
        <h3
          className={cn(
            "mt-2 font-display font-medium leading-snug tracking-tight line-clamp-2",
            size === "large" ? "text-2xl md:text-[2.4rem] md:leading-tight" : "text-[1.8rem]",
          )}
        >
          <span lang={name.primaryLang}>{name.primary}</span>
        </h3>
        {name.secondary && (
          <p lang={name.secondaryLang} className="mt-1 text-sm text-muted-foreground line-clamp-1">{name.secondary}</p>
        )}

        {degradation && (
          <p className="mt-3 flex items-start gap-2 text-sm text-muted-foreground">
            <Clock className="mt-[3px] h-3.5 w-3.5 shrink-0 opacity-60" />
            <span lang="en" className="line-clamp-2">{degradation}</span>
          </p>
        )}

        <div className="mt-auto flex items-center justify-between gap-3 pt-6">
          <div className="flex flex-wrap items-center gap-3">
            {material.trl_level ? <TRLDots level={material.trl_level} /> : null}
          </div>
          <span className="arrow-nudge font-mono text-sm text-muted-foreground group-hover:text-primary transition-colors">→</span>
        </div>
      </div>
    </Link>
  );
};

export default MaterialCard;
